/**
 * Homepage: "how it works" pipeline stepper for the product hierarchy
 * (Season → Competitions → Game Plans → Game Analytics → Data Coach).
 * Sits between Hero and the interactive LayeredExplainer demo.
 *
 * Mechanic: a vertical rail of five stage tabs joined by a spine that fills
 * as you move down the pipeline; the selected stage opens in a panel to the
 * right. While the section is in view it auto-plays (story-style progress
 * bar on the active tab); any click / key stops auto-play and hands control
 * to the visitor. Mobile: the rail becomes a row of tappable icon chips,
 * no auto-play (avoids shifting the page under the reader's thumb).
 *
 * Progressive enhancement: every panel is real semantic HTML in the DOM.
 * The stepper only switches on when JS runs AND prefers-reduced-motion is
 * not set (class .zh-on); otherwise all five panels render stacked, fully
 * visible, with no motion.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const STEP_MS = 6500;

/* Same glyphs as LayeredExplainer's LAYER_ICONS, for continuity with the demo below. */
const ICONS = {
  season: (<><rect x="4" y="6" width="24" height="21" rx="4" /><path d="M4 12.5h24M11 3.5v5M21 3.5v5" /><path d="m12 19.5 3 3 6-6" /></>),
  competitions: (<><path d="M10 5h12v6a6 6 0 0 1-12 0z" /><path d="M10 7H6a4 4 0 0 0 4 5M22 7h4a4 4 0 0 1-4 5" /><path d="M16 17v5M11 27h10M12.5 22h7v5h-7z" /></>),
  gamePlans: (<><rect x="6.5" y="5" width="19" height="23" rx="3.5" /><path d="M12 5V3.5h8V5" /><path d="M11 13h10M11 18h10M11 23h6" /></>),
  gameAnalytics: (<><path d="M5 27h22" /><path d="M8.5 22v-5M14 22V11M19.5 22v-7M25 22V6" strokeWidth="3" /></>),
  dataCoach: (<><path d="M5 8.5A3.5 3.5 0 0 1 8.5 5h15A3.5 3.5 0 0 1 27 8.5v10a3.5 3.5 0 0 1-3.5 3.5H14l-6 5v-5h.5" /><path d="m10.5 16 3.2-3.5 3 2.4 4.8-5" /></>),
};

function Glyph({ children, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

const HEADLINE = 'Gamify your racket sport development';
const SUBLINE = 'Write down what you learn, build your own knowledge bank — and let Data Coach surface the details that matter.';

const STAGES = [
  {
    key: 'season', num: '01', title: 'Season', color: 'amber',
    tag: 'Decide what you’re chasing',
    lead: 'Set the goals you’re chasing this season — and watch them move.',
    items: [
      'Break each goal into milestones',
      'Link your leagues and tournaments to the season',
      'Track progress in Game Analytics charts',
    ],
  },
  {
    key: 'competitions', num: '02', title: 'Competitions', color: 'purple',
    tag: 'Every league and tournament',
    lead: 'Bring in the leagues and tournaments you actually play.',
    items: [
      'Leagues — win rate and form tracked round by round',
      'Tournaments — plan, readiness and goal going in; notes and ratings coming out',
      'Data Coach reads both your words and your numbers',
    ],
  },
  {
    key: 'gamePlans', num: '03', title: 'Game Plans', color: 'accent',
    tag: 'Prepare. Play. Rate.',
    lead: 'Walk into every match with a plan — walk out knowing how it went.',
    halves: [
      {
        t: 'Before', items: [
          'Opponent, format and the round it belongs to',
          'Your goal, tactics and focus areas',
          'Prep notes from Data Coach — patterns vs. this opponent, recent form, what’s worked',
          'Optional: readiness, racket & shoes — and your ideal restring point*',
        ],
      },
      {
        t: 'After', items: [
          'Result and score',
          'Did your plan hold up?',
          '1–5 stars for every part of your game',
          'Duration, heart rate and calories, if you track them',
        ],
      },
    ],
    aside: 'Rather talk than type? Dictate your plan and reflection.',
    footnote: '*strung-racket sports',
    footer: 'Every plan grows your record — revealing patterns no single result can show.',
  },
  {
    key: 'gameAnalytics', num: '04', title: 'Game Analytics', color: 'teal',
    tag: 'Your facts, your insight',
    lead: 'The facts on your game — the insight is yours.',
    items: [
      'Season dashboard — See the trend behind your season — the facts on where you’re improving, and where there’s room to grow.',
      'Performance trend — Real data on how your form has moved over time, so you know exactly where to focus your development.',
      'Game-area breakdown — See how each area of your game — serve, return, forehand, backhand, net — has trended over time, so you can assess your own progress and know where to focus.',
      'Heads-Up — The full record of how your matches against each opponent have gone — information you can build your game around.',
      'Competition reports — The real facts behind your leagues and tournaments, not just how a weekend felt — so you know where to raise your level.',
      'Racket & stringing stats — Data on how your racket and stringing are actually performing, so you can make informed decisions about your gear.',
    ],
  },
  {
    key: 'dataCoach', num: '05', title: 'Data Coach', color: 'green',
    tag: 'Patterns in your data',
    lead: 'A powerful AI assistant that spots valuable patterns and trends from large amounts of data.',
    items: [
      'Reads your entire match history — not just your last result — to surface what’s actually going on.',
      'Finds patterns across matches and opponents that would be easy to miss.',
      'Combines your written reflections with the numbers behind them from the large set of data, to discover the most valuable insights.',
      'Powered by Claude, Anthropic’s AI, as the engine for the Data Coach.',
    ],
  },
];

function Panel({ s, i, active, next, onNext }) {
  let n = 0; // stagger index for the reveal cascade
  const li = (t) => <li key={t} style={{ '--i': n++ }}>{t}</li>;
  return (
    <section
      id={`zh-p-${s.key}`}
      role="tabpanel"
      aria-labelledby={`zh-${s.key}`}
      className={`zh-panel${active ? ' is-active' : ''}`}
      style={{ '--c': s.cv }}
    >
      <header className="zh-ph">
        <span className="zh-pico"><Glyph size={22}>{ICONS[s.key]}</Glyph></span>
        <span className="zh-pnum">{s.num}</span>
        <h3 id={`zh-${s.key}`} className="zh-ptitle">{s.title}</h3>
      </header>
      <p className="zh-lead" style={{ '--i': n++ }}>{s.lead}</p>

      {s.items && <ul className="zh-list">{s.items.map(li)}</ul>}

      {s.halves && (
        <div className="zh-halves">
          {s.halves.map((h) => (
            <div key={h.t} className="zh-half">
              <span className="zh-htag" style={{ '--i': n++ }}>{h.t}</span>
              <ul className="zh-list">{h.items.map(li)}</ul>
            </div>
          ))}
        </div>
      )}
      {s.aside && <p className="zh-aside" style={{ '--i': n++ }}>{s.aside}</p>}

      {s.chips && <ul className="zh-chips">{s.chips.map(li)}</ul>}

      {s.footer && <p className="zh-foot" style={{ '--i': n++ }}>{s.footer}{s.footnote && <small> {s.footnote}</small>}</p>}

      {next && (
        <button type="button" className="zh-next" onClick={() => onNext(i + 1)} tabIndex={active ? 0 : -1}>
          Feeds into <strong style={{ color: next.cv }}>{next.title}</strong> <span aria-hidden="true">→</span>
        </button>
      )}
    </section>
  );
}

export default function HierarchyFlow({ D }) {
  const rootRef = useRef(null);
  const tabRefs = useRef([]);
  const [on, setOn] = useState(false);        // stepper enhancement active
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(false);    // auto-play allowed (desktop, not yet interacted)
  const [inView, setInView] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mm = window.matchMedia;
    if (mm && mm('(prefers-reduced-motion: reduce)').matches) return undefined;
    setOn(true);
    const desktop = mm ? mm('(min-width: 860px)').matches : true;
    if (!desktop || !('IntersectionObserver' in window) || !rootRef.current) return undefined;
    setAuto(true);
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.45 });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  const pick = useCallback((i, focus) => {
    setAuto(false);
    setIdx(i);
    if (focus && tabRefs.current[i]) tabRefs.current[i].focus();
  }, []);

  const onKey = (e) => {
    const k = e.key;
    let t = null;
    if (k === 'ArrowDown' || k === 'ArrowRight') t = (idx + 1) % STAGES.length;
    else if (k === 'ArrowUp' || k === 'ArrowLeft') t = (idx + STAGES.length - 1) % STAGES.length;
    else if (k === 'Home') t = 0;
    else if (k === 'End') t = STAGES.length - 1;
    if (t !== null) { e.preventDefault(); pick(t, true); }
  };

  const stages = STAGES.map((s) => ({ ...s, cv: D[s.color] || D.accent }));
  const running = on && auto && inView && !hover;
  const fill = `${(idx / (STAGES.length - 1)) * 100}%`;

  return (
    <section
      className={`zh${on ? ' zh-on' : ''}`}
      aria-labelledby="zh-heading"
      ref={rootRef}
      style={{
        '--bg0': D.bg0, '--bg1': D.bg1, '--bg2': D.bg2, '--border': D.border,
        '--text': D.text, '--dim': D.dim, '--accent': D.accent,
        '--cur': stages[idx].cv,
        fontFamily: D.font,
      }}
    >
      <style>{CSS}</style>
      <div className="zh-wrap">
        <header className="zh-head">
          <span className="zh-kicker">How it works{on && <span className="zh-hint"> — tap a step</span>}</span>
          <h2 id="zh-heading" className="zh-headline">{HEADLINE}</h2>
          <p className="zh-subline">{SUBLINE}</p>
        </header>

        <div className="zh-stage">
          {on && (
            <div className="zh-rail" role="tablist" aria-label="How ZenAnalytics works, step by step"
              aria-orientation="vertical" onKeyDown={onKey}
              onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
              <span className="zh-spine" aria-hidden="true"><span className="zh-spine-fill" style={{ height: fill, width: fill }} /></span>
              {stages.map((s, i) => {
                const sel = i === idx;
                return (
                  <button
                    key={s.key}
                    ref={(el) => { tabRefs.current[i] = el; }}
                    type="button"
                    role="tab"
                    id={`zh-t-${s.key}`}
                    aria-selected={sel}
                    aria-controls={`zh-p-${s.key}`}
                    tabIndex={sel ? 0 : -1}
                    className={`zh-tab${sel ? ' is-sel' : ''}${i < idx ? ' is-done' : ''}`}
                    style={{ '--c': s.cv }}
                    onClick={() => pick(i)}
                  >
                    <span className="zh-tico"><Glyph size={18}>{ICONS[s.key]}</Glyph></span>
                    <span className="zh-ttext">
                      <span className="zh-tnum">{s.num}</span>
                      <span className="zh-tname">{s.title}</span>
                      <span className="zh-ttag">{s.tag}</span>
                    </span>
                    {sel && auto && (
                      <span className="zh-prog" aria-hidden="true">
                        <span
                          key={`p${idx}`}
                          className="zh-prog-bar"
                          style={{ animationDuration: `${STEP_MS}ms`, animationPlayState: running ? 'running' : 'paused' }}
                          onAnimationEnd={() => setIdx((v) => (v + 1) % STAGES.length)}
                        />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="zh-panels" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            {stages.map((s, i) => (
              <Panel key={s.key} s={s} i={i} active={!on || i === idx}
                next={stages[i + 1]} onNext={(t) => pick(t)} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const CSS = `
.zh { background: var(--bg0); color: var(--text); padding: 44px 16px 48px; }
.zh *, .zh *::before, .zh *::after { box-sizing: border-box; }
.zh-wrap { max-width: 1120px; margin: 0 auto; }
.zh-head { max-width: 680px; margin: 0 auto 26px; text-align: center; }
.zh-kicker { display: inline-block; font-family: ${MONO}; font-size: 11px; letter-spacing: .14em; text-transform: uppercase;
  color: var(--cur); margin-bottom: 10px; transition: color .5s; }
.zh-hint { color: var(--dim); text-transform: none; letter-spacing: .04em; }
.zh-headline { margin: 0 0 10px; font-size: clamp(24px, 5.4vw, 38px); line-height: 1.12; font-weight: 800; letter-spacing: -.02em; color: var(--text); }
.zh-subline { margin: 0; font-size: clamp(14.5px, 3.4vw, 17px); line-height: 1.5; color: var(--dim); font-weight: 500; }

/* ---------- panels (static fallback = all stacked) ---------- */
.zh-panels { display: flex; flex-direction: column; gap: 10px; }
.zh-panel { position: relative; min-width: 0; background: var(--bg1); border: 1px solid var(--border);
  border-left: 3px solid var(--c); border-radius: 14px; padding: 18px 18px 16px; overflow: hidden; }
.zh-ph { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.zh-pico { flex: none; width: 38px; height: 38px; border-radius: 11px; display: grid; place-items: center; color: var(--c);
  background: color-mix(in srgb, var(--c) 14%, var(--bg1)); border: 1px solid color-mix(in srgb, var(--c) 35%, var(--border)); }
.zh-pnum { font-family: ${MONO}; font-size: 11px; color: var(--dim); letter-spacing: .1em; }
.zh-ptitle { margin: 0; font-size: 13px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: var(--c); }
.zh-lead { margin: 0 0 12px; font-size: clamp(18px, 4.4vw, 23px); line-height: 1.28; font-weight: 750; letter-spacing: -.01em; color: var(--text); }

.zh-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 7px; }
.zh-list li { position: relative; padding-left: 20px; font-size: 14.5px; line-height: 1.42; color: var(--text); overflow-wrap: anywhere; }
.zh-list li::before { content: ""; position: absolute; left: 2px; top: .36em; width: 9px; height: 5px;
  border-left: 2px solid var(--c); border-bottom: 2px solid var(--c); transform: rotate(-45deg); }

.zh-halves { display: grid; gap: 14px; }
.zh-htag { display: inline-block; font-family: ${MONO}; font-size: 10.5px; letter-spacing: .14em; text-transform: uppercase;
  color: var(--c); padding: 3px 8px; border-radius: 999px; margin-bottom: 8px;
  background: color-mix(in srgb, var(--c) 12%, transparent); border: 1px solid color-mix(in srgb, var(--c) 30%, transparent); }
.zh-aside { margin: 12px 0 0; font-size: 13.5px; color: var(--dim); }
.zh-foot { margin: 12px 0 0; padding-top: 11px; border-top: 1px dashed color-mix(in srgb, var(--c) 35%, var(--border));
  font-size: 14px; line-height: 1.45; color: var(--text); font-style: italic; }
.zh-foot small { font-style: normal; color: var(--dim); font-size: 11.5px; }

.zh-chips { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px; }
.zh-chips li { font-size: 13.5px; font-weight: 600; padding: 8px 12px; border-radius: 10px; color: var(--text);
  background: color-mix(in srgb, var(--c) 9%, var(--bg2)); border: 1px solid color-mix(in srgb, var(--c) 28%, var(--border)); }

.zh-next { display: none; }

/* ---------- stepper (JS on, motion allowed) ---------- */
.zh-on .zh-stage { display: flex; flex-direction: column; gap: 14px; }

/* mobile rail: horizontal icon chips */
.zh-rail { position: relative; display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px; }
.zh-spine { position: absolute; left: 10%; right: 10%; top: 22px; height: 2px; background: var(--border); border-radius: 2px; }
.zh-spine-fill { position: absolute; left: 0; top: 0; height: 100% !important; border-radius: inherit;
  background: linear-gradient(90deg, var(--accent), var(--cur)); transition: width .6s cubic-bezier(.3,.7,.2,1), background .5s; }
.zh-tab { position: relative; appearance: none; border: 0; background: none; color: var(--dim); font: inherit; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 0 0 4px; text-align: center; min-width: 0;
  -webkit-tap-highlight-color: transparent; }
.zh-tico { position: relative; z-index: 1; width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center;
  background: var(--bg1); border: 1.5px solid var(--border); color: var(--dim);
  transition: transform .35s cubic-bezier(.3,1.5,.5,1), background .3s, border-color .3s, color .3s, box-shadow .4s; }
.zh-tab.is-done .zh-tico { color: var(--c); border-color: color-mix(in srgb, var(--c) 55%, var(--border)); }
.zh-tab.is-sel .zh-tico { color: var(--bg0); background: var(--c); border-color: var(--c); transform: scale(1.1);
  box-shadow: 0 0 0 5px color-mix(in srgb, var(--c) 18%, transparent), 0 8px 24px -6px color-mix(in srgb, var(--c) 60%, transparent); }
.zh-ttext { display: flex; flex-direction: column; min-width: 0; }
.zh-tnum, .zh-ttag { display: none; }
.zh-tname { font-size: 10.5px; font-weight: 700; line-height: 1.2; letter-spacing: .02em; transition: color .3s; }
.zh-tab.is-sel .zh-tname { color: var(--c); }
.zh-tab:focus-visible { outline: none; }
.zh-tab:focus-visible .zh-tico { outline: 2px solid var(--c); outline-offset: 3px; }
.zh-prog { display: none; }

/* panels: only the active one shown on mobile */
.zh-on .zh-panel { display: none; }
.zh-on .zh-panel.is-active { display: block; animation: zh-in .45s cubic-bezier(.2,.7,.2,1) both; }
.zh-on .zh-panel { border-left-width: 1px; border-top: 3px solid var(--c); }
.zh-on .zh-panel::after { content: ""; position: absolute; right: -60px; top: -80px; width: 240px; height: 240px; border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--c) 16%, transparent), transparent 70%); pointer-events: none; }
.zh-on .zh-panel.is-active .zh-lead,
.zh-on .zh-panel.is-active .zh-htag,
.zh-on .zh-panel.is-active li,
.zh-on .zh-panel.is-active .zh-aside,
.zh-on .zh-panel.is-active .zh-foot { animation: zh-rise .5s cubic-bezier(.2,.7,.2,1) both; animation-delay: calc(70ms + var(--i, 0) * 45ms); }
.zh-on .zh-next { display: inline-flex; align-items: center; gap: 6px; margin-top: 14px; appearance: none; background: none;
  border: 1px solid var(--border); color: var(--dim); font: inherit; font-size: 13px; padding: 7px 12px; border-radius: 999px; cursor: pointer;
  transition: border-color .2s, color .2s, transform .2s; }
.zh-on .zh-next:hover, .zh-on .zh-next:focus-visible { color: var(--text); border-color: var(--c); transform: translateX(2px); outline: none; }

@keyframes zh-in { from { opacity: 0; transform: translateY(10px) scale(.99); } to { opacity: 1; transform: none; } }
@keyframes zh-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes zh-prog { from { transform: scaleX(0); } to { transform: scaleX(1); } }

@media (min-width: 640px) {
  .zh-halves { grid-template-columns: 1fr 1fr; gap: 24px; }
  .zh-panel { padding: 22px 26px 20px; }
}

@media (min-width: 860px) {
  .zh { padding: 64px 24px 68px; }
  .zh-head { margin-bottom: 36px; }
  .zh-on .zh-stage { display: grid; grid-template-columns: 290px 1fr; gap: 28px; align-items: stretch; }

  /* vertical rail */
  .zh-rail { display: flex; flex-direction: column; gap: 4px; align-self: start; }
  .zh-spine { left: 31px; right: auto; top: 34px; bottom: 34px; width: 2px; height: auto; }
  .zh-spine-fill { width: 100% !important; background: linear-gradient(180deg, var(--accent), var(--cur));
    transition: height .6s cubic-bezier(.3,.7,.2,1), background .5s; }
  .zh-tab { flex-direction: row; align-items: center; gap: 14px; padding: 12px 14px 12px 10px; border-radius: 14px; text-align: left;
    transition: background .3s; }
  .zh-tab:hover { background: color-mix(in srgb, var(--c) 6%, transparent); }
  .zh-tab.is-sel { background: var(--bg1); box-shadow: inset 0 0 0 1px var(--border); }
  .zh-tnum { display: block; font-family: ${MONO}; font-size: 10px; letter-spacing: .1em; color: var(--dim); }
  .zh-tname { font-size: 15px; letter-spacing: 0; color: var(--text); }
  .zh-tab:not(.is-sel) .zh-tname { color: var(--dim); }
  .zh-ttag { display: block; font-size: 12.5px; color: var(--dim); margin-top: 1px; max-height: 0; opacity: 0; overflow: hidden;
    transition: max-height .35s, opacity .35s; }
  .zh-tab.is-sel .zh-ttag { max-height: 20px; opacity: 1; }
  .zh-prog { display: block; position: absolute; left: 66px; right: 16px; bottom: 7px; height: 2px; border-radius: 2px;
    background: color-mix(in srgb, var(--c) 15%, transparent); overflow: hidden; }
  .zh-prog-bar { display: block; height: 100%; background: var(--c); transform-origin: left; animation-name: zh-prog;
    animation-timing-function: linear; animation-fill-mode: both; }

  /* panels stacked in one grid cell: height = tallest panel, no layout jump */
  .zh-on .zh-panels { display: grid; }
  .zh-on .zh-panel { display: block; grid-area: 1 / 1; visibility: hidden; opacity: 0; transform: translateY(10px);
    transition: opacity .35s, transform .45s cubic-bezier(.2,.7,.2,1), visibility 0s .45s; animation: none; }
  .zh-on .zh-panel.is-active { visibility: visible; opacity: 1; transform: none; transition: opacity .4s .05s, transform .5s .05s cubic-bezier(.2,.7,.2,1); animation: none; }
  .zh-on .zh-panel { padding: 30px 34px 26px; }
  .zh-on .zh-lead { font-size: 27px; margin-bottom: 18px; max-width: 30ch; }
}

@media (prefers-reduced-motion: reduce) {
  .zh *, .zh *::before, .zh *::after { animation: none !important; transition: none !important; }
}
`;
